import { createSelector } from 'reselect'
import { getWaiter } from 'redux-waiter'

import userSession from 'store/modules/user-session'
import persons from 'store/modules/persons'

import {
  getIsNewExpenseRequestDisabled,
  getNewExpenseRequestDisabledReason,
} from '../utils/expenses'
import { GET_EXPENSE_REQUESTS_NAME, NAME } from './constants'

export const getExpenseRequests = createSelector(
  (state) => state[NAME],
  (expenseRequests) => expenseRequests.expenses
)

export const getFamilyMembers = createSelector(
  (state) => state[NAME],
  (expenseRequest) => expenseRequest.familyBenefits
)

export const getSelectedLifeEvent = createSelector(
  (state) => state[NAME],
  (expenseRequests) => expenseRequests.selectedLifeEvent
)

export const getSelectedExpense = createSelector(
  (state) => state[NAME],
  (expenseRequests) => expenseRequests.selectedExpense
)

export const getLineItemsReceipts = createSelector(
  (state) => state[NAME],
  (expenseRequests) => expenseRequests.lineItems
)

export const getTotalAmountRequested = (id, state) =>
  createSelector(
    () => state[NAME],
    (expenseRequest) => {
      if (!id || !expenseRequest.receipts[id]) return 0
      const { data } = expenseRequest.receipts[id]
      return data.reduce((prev, curr) => {
        const total = prev + Number(curr.amount)
        return total
      }, 0)
    }
  )(state)

export const getSelectedExpenseId = createSelector(
  (state) => state[NAME],
  (expenseRequests) => expenseRequests.selectedExpenseId
)

export const getUiConfig = createSelector(
  (state) => state[NAME],
  (expenseRequests) => expenseRequests.ui
)

export const getExpenseReceipts = (id, state) =>
  createSelector(
    () => state[NAME],
    (expenseRequest) => expenseRequest.receipts[id]
  )(state)

export const getRecordPointer = createSelector(
  (state) => state[NAME],
  (temp) => temp.recordPointer
)

export const getSelectedYear = createSelector(
  (state) => state[NAME],
  (temp) => temp.year
)

export const hasMore = createSelector(
  getSelectedLifeEvent,
  getRecordPointer,
  (lifeEvent, recordPointer) => lifeEvent && lifeEvent.reimbursements.length > recordPointer
)

export const isExpenseAvailable = createSelector(
  (state) => state[NAME],
  (expenseRequests) => expenseRequests.available
)

export const isExpenseRequestsLoading = (state) => {
  const clientId = userSession.selectors.getClientId(state)
  const employeeKey = persons.selectors.getEmployeeKey(state)
  const benefitYear = getSelectedYear(state)

  return getWaiter(
    state,
    `${GET_EXPENSE_REQUESTS_NAME}_${employeeKey}_${clientId}_${benefitYear}`
  ).isPending
}

export const isNewExpenseRequestDisabled = createSelector(
  getSelectedLifeEvent,
  isExpenseAvailable,
  isExpenseRequestsLoading,
  (selectedLifeEvent, available, isLoading) =>
    getIsNewExpenseRequestDisabled({
      selectedLifeEvent,
      isExpenseAvailable: available,
      isLoading,
    })
)

export const newExpenseRequestDisabledReason = createSelector(
  getSelectedLifeEvent,
  isExpenseAvailable,
  getSelectedYear,
  isExpenseRequestsLoading,
  (selectedLifeEvent, available, selectedYear, isLoading) =>
    getNewExpenseRequestDisabledReason({
      selectedLifeEvent,
      isExpenseAvailable: available,
      selectedYear,
      isLoading,
    })
)

export const getExpenseReimbursementData = createSelector(
  (state) => state[NAME],
  (expenseRequests) => ({
    selectedLifeEvent: expenseRequests.selectedLifeEvent,
    selectedExpense: expenseRequests.selectedExpense,
    ui: expenseRequests.ui,
    expenseId: expenseRequests.selectedExpenseId,
    year: expenseRequests.year,
    family: expenseRequests.familyBenefits,
  })
)
